import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { CentralCatalogueService } from '../../services/central-catalogue/central-catalogue.service';
import { ToastService } from '../../services/toast/toast.service';
import { PermissionService } from '../../services/permission/permission.service';
import { LoaderComponent } from '../../atoms/loader/loader.component';
import { CcOrgDialogComponent } from './cc-org-dialog/cc-org-dialog.component';
import { CcPremisesDialogComponent } from './cc-premises-dialog/cc-premises-dialog.component';
import { CcSetDialogComponent } from './cc-set-dialog/cc-set-dialog.component';
import { CcRoomDialogComponent } from './cc-room-dialog/cc-room-dialog.component';
import { CcStateDialogComponent } from './cc-state-dialog/cc-state-dialog.component';
import { CcBlockDialogComponent } from './cc-block-dialog/cc-block-dialog.component';

type CatalogueTab = 'organisations' | 'premises' | 'sets' | 'rooms' | 'instances';

// the central location's own inventory: organisation -> premises -> guest house
// -> room, plus the per-day instances (view, block/unblock, generate).
@Component({
  selector: 'app-oa-room-catalogue',
  standalone: true,
  imports: [CommonModule, FormsModule, LoaderComponent],
  templateUrl: './oa-room-catalogue.component.html',
  styleUrl: './oa-room-catalogue.component.scss',
})
export class OaRoomCatalogueComponent implements OnInit {
  activeTab: CatalogueTab = 'premises';
  isLoading: boolean = false;
  canManage: boolean = false;
  canBlock: boolean = false;
  canGenerate: boolean = false;
  searchText: string = '';

  organisations: any[] = [];
  premises: any[] = [];
  sets: any[] = []; // the Guest houses tab (respects its filters)
  allSets: any[] = []; // unfiltered — feeds the Rooms tab filter and dialogs
  rooms: any[] = [];
  instances: any[] = [];

  // filters
  setsPremisesFilter: number | '' = '';
  setsTypeFilter: string = '';
  roomsSetFilter: number | '' = '';
  instanceDate: string = '';
  selectedInstances = new Set<number>();

  // several tabs load two lists at once; the loader stays until all are done
  private pendingLoads = 0;

  constructor(
    private catalogueService: CentralCatalogueService,
    private toastService: ToastService,
    private permissionService: PermissionService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.canManage = this.permissionService.can('room-catalogue', 'can_manage');
    this.canBlock = this.permissionService.can('room-catalogue', 'can_block');
    this.canGenerate = this.permissionService.can('room-catalogue', 'can_generate');
    this.instanceDate = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
    this.loadOrganisations();
    this.loadPremises();
  }

  setTab(tab: CatalogueTab): void {
    this.activeTab = tab;
    this.searchText = '';
    if (tab === 'sets' && !this.sets.length) this.loadSets();
    if (tab === 'rooms') {
      if (!this.allSets.length) this.loadAllSets();
      if (!this.rooms.length) this.loadRooms();
    }
    if (tab === 'instances' && !this.instances.length) this.loadInstances();
  }

  private startLoad(): void {
    this.pendingLoads++;
    this.isLoading = true;
  }

  private endLoad(): void {
    this.pendingLoads = Math.max(0, this.pendingLoads - 1);
    if (!this.pendingLoads) this.isLoading = false;
  }

  private matches(values: any[]): boolean {
    const q = this.searchText.trim().toLowerCase();
    if (!q) return true;
    return values.some((v) => (v ?? '').toString().toLowerCase().includes(q));
  }

  // -- loads -----------------------------------------------------------------

  loadOrganisations(): void {
    this.catalogueService.listOrganisations().subscribe({
      next: (res: any) => (this.organisations = res?.data?.organisations || []),
      error: (err) => this.toastService.error(err?.error?.message || 'Failed to load organisations.'),
    });
  }

  loadPremises(): void {
    this.startLoad();
    this.catalogueService.listPremises().subscribe({
      next: (res: any) => {
        this.premises = res?.data?.premises || [];
        this.endLoad();
      },
      error: (err) => {
        this.endLoad();
        this.toastService.error(err?.error?.message || 'Failed to load premises.');
      },
    });
  }

  loadSets(): void {
    this.startLoad();
    this.catalogueService
      .listSets(this.setsPremisesFilter || undefined, this.setsTypeFilter || undefined)
      .subscribe({
        next: (res: any) => {
          this.sets = res?.data?.sets || [];
          this.endLoad();
        },
        error: (err) => {
          this.endLoad();
          this.toastService.error(err?.error?.message || 'Failed to load guest houses.');
        },
      });
  }

  // the complete list, regardless of the Guest houses tab filters
  loadAllSets(): void {
    this.startLoad();
    this.catalogueService.listSets().subscribe({
      next: (res: any) => {
        this.allSets = res?.data?.sets || [];
        this.endLoad();
      },
      error: (err) => {
        this.endLoad();
        this.toastService.error(err?.error?.message || 'Failed to load guest houses.');
      },
    });
  }

  loadRooms(): void {
    this.startLoad();
    this.catalogueService.listAccommodations(this.roomsSetFilter || undefined).subscribe({
      next: (res: any) => {
        this.rooms = res?.data?.accommodations || [];
        this.endLoad();
      },
      error: (err) => {
        this.endLoad();
        this.toastService.error(err?.error?.message || 'Failed to load rooms.');
      },
    });
  }

  loadInstances(): void {
    if (!this.instanceDate) {
      this.toastService.error('Pick a date first.');
      return;
    }
    this.startLoad();
    this.selectedInstances.clear();
    this.catalogueService.listInstances(this.instanceDate).subscribe({
      next: (res: any) => {
        this.instances = res?.data?.instances || [];
        this.endLoad();
      },
      error: (err) => {
        this.endLoad();
        this.toastService.error(err?.error?.message || 'Failed to load instances.');
      },
    });
  }

  // -- filtered views ----------------------------------------------------------

  get filteredOrganisations(): any[] {
    return this.organisations.filter((o) => this.matches([o.name, o.description, o.address]));
  }

  get filteredPremises(): any[] {
    return this.premises.filter((p) => this.matches([p.name, p.abbrevName, p.orgName, p.address]));
  }

  get filteredSets(): any[] {
    return this.sets.filter((s) =>
      this.matches([s.name, s.abbrevName, s.premisesName, s.type, s.category])
    );
  }

  get filteredRooms(): any[] {
    return this.rooms.filter((r) =>
      this.matches([r.abbrevName, r.setName, r.category, String(r.accommodationNumber)])
    );
  }

  get filteredInstances(): any[] {
    return this.instances.filter((i) =>
      this.matches([i.roomCode, i.guestHouse, i.premisesName, i.status])
    );
  }

  // -- dialogs ------------------------------------------------------------------

  private open(component: any, data: any, onChange: () => void): void {
    const ref = this.dialog.open(component, { width: '760px', maxWidth: '95vw', data });
    ref.afterClosed().subscribe((changed: boolean) => {
      if (changed) onChange();
    });
  }

  addOrganisation(): void {
    this.open(CcOrgDialogComponent, {}, () => this.loadOrganisations());
  }

  editOrganisation(org: any): void {
    this.open(CcOrgDialogComponent, { organisation: org }, () => this.loadOrganisations());
  }

  addPremises(): void {
    this.open(CcPremisesDialogComponent, { organisations: this.organisations }, () =>
      this.loadPremises()
    );
  }

  editPremises(row: any): void {
    this.open(CcPremisesDialogComponent, { premises: row, organisations: this.organisations }, () =>
      this.loadPremises()
    );
  }

  addSet(): void {
    this.open(CcSetDialogComponent, { premisesList: this.premises }, () => this.refreshSets());
  }

  editSet(row: any): void {
    this.open(CcSetDialogComponent, { set: row, premisesList: this.premises }, () =>
      this.refreshSets()
    );
  }

  changeSetState(row: any): void {
    this.open(
      CcStateDialogComponent,
      { kind: 'set', name: row.name, ids: [row.setId], currentStatus: row.status },
      () => this.refreshSets()
    );
  }

  private refreshSets(): void {
    this.loadSets();
    if (this.allSets.length) this.loadAllSets();
  }

  addRoom(): void {
    if (!this.allSets.length) {
      this.toastService.error('Guest houses are still loading. Try again in a moment.');
      return;
    }
    this.open(CcRoomDialogComponent, { sets: this.allSets }, () => this.loadRooms());
  }

  editRoom(row: any): void {
    this.open(CcRoomDialogComponent, { room: row, sets: this.allSets }, () => this.loadRooms());
  }

  changeRoomState(row: any): void {
    this.open(
      CcStateDialogComponent,
      {
        kind: 'room',
        name: `${row.setName} · ${row.abbrevName}`,
        ids: [row.accommodationId],
        currentStatus: row.status,
      },
      () => this.loadRooms()
    );
  }

  // -- instances ------------------------------------------------------------------

  instanceKey(row: any): string {
    return String(row?.instanceId ?? '');
  }

  toggleInstance(row: any): void {
    const id = Number(row.instanceId);
    if (this.selectedInstances.has(id)) this.selectedInstances.delete(id);
    else this.selectedInstances.add(id);
  }

  isInstanceSelected(row: any): boolean {
    return this.selectedInstances.has(Number(row.instanceId));
  }

  blockSelected(action: 'BLOCK' | 'UNBLOCK'): void {
    // act only on rows the operator can currently SEE: the search box may have
    // hidden earlier ticks, and hidden rows must never change silently.
    const visible = new Set(this.filteredInstances.map((i) => Number(i.instanceId)));
    const ids = Array.from(this.selectedInstances).filter((id) => visible.has(id));
    if (!ids.length) {
      this.toastService.error('Select at least one visible room-day first.');
      return;
    }
    this.open(CcBlockDialogComponent, { action, instanceIds: ids }, () => this.loadInstances());
  }

  generate(): void {
    this.startLoad();
    this.catalogueService.generateInstances().subscribe({
      next: (res: any) => {
        this.endLoad();
        const data = res?.data || {};
        this.toastService.success(
          data.date ? `${res?.message} Date: ${data.date}.` : res?.message || 'Generation finished.'
        );
        // jump to the generated date so the new rows are visible at once
        if (data.date) this.instanceDate = data.date;
        this.loadInstances();
      },
      error: (err) => {
        this.endLoad();
        this.toastService.error(err?.error?.message || 'Generation failed.');
      },
    });
  }
}
